class CartDrawer extends HTMLElement {
    constructor() {
        super();

        this.addEventListener(
            "keyup",
            (evt) => evt.code === "Escape" && this.close(),
        );
        this.querySelector("#CartDrawer-Overlay").addEventListener(
            "click",
            this.close.bind(this),
        );
        this.setHeaderCartIconAccessibility();
    }

    setHeaderCartIconAccessibility() {
        const cartLink = document.querySelector("#cart-icon-bubble");
        if (!cartLink) return;

        cartLink.setAttribute("role", "button");
        cartLink.setAttribute("aria-haspopup", "dialog");
        cartLink.addEventListener("click", (event) => {
            event.preventDefault();
            this.open(cartLink);
        });
        cartLink.addEventListener("keydown", (event) => {
            if (event.code.toUpperCase() === "SPACE") {
                event.preventDefault();
                this.open(cartLink);
            }
        });
    }

    open(triggeredBy) {
        if (triggeredBy) this.setActiveElement(triggeredBy);
        const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
        if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
            this.setSummaryAccessibility(cartDrawerNote);
        // here the animation doesn't seem to always get triggered. A timeout seem to help
        setTimeout(() => {
            this.classList.add("animate", "active");
        });

        this.addEventListener(
            "transitionend",
            () => {
                const containerToTrapFocusOn = this.classList.contains(
                    "is-empty",
                )
                    ? this.querySelector(".drawer__inner-empty")
                    : document.getElementById("CartDrawer");
                const focusElement =
                    this.querySelector(".drawer__inner") ||
                    this.querySelector(".drawer__close");
                trapFocus(containerToTrapFocusOn, focusElement);
            },
            { once: true },
        );

        document.body.classList.add("overflow-hidden");

        requestAnimationFrame(() => {
            this.applyRecommendationFilter(
                window.cartDrawerLockedRecommendationKey ||
                    window.lastAddedCartItemKey,
            );
        });

        this.enforceOnePerCustomerLimits();
    }

    close() {
        this.classList.remove("active");
        removeTrapFocus(this.activeElement);
        document.body.classList.remove("overflow-hidden");
    }

    setSummaryAccessibility(cartDrawerNote) {
        cartDrawerNote.setAttribute("role", "button");
        cartDrawerNote.setAttribute("aria-expanded", "false");

        if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
            cartDrawerNote.setAttribute(
                "aria-controls",
                cartDrawerNote.nextElementSibling.id,
            );
        }

        cartDrawerNote.addEventListener("click", (event) => {
            event.currentTarget.setAttribute(
                "aria-expanded",
                !event.currentTarget.closest("details").hasAttribute("open"),
            );
        });

        cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
    }

    renderContents(parsedState) {
        this.querySelector(".drawer__inner").classList.contains("is-empty") &&
            this.querySelector(".drawer__inner").classList.remove("is-empty");
        this.productId = parsedState.id;

        this.getSectionsToRender().forEach((section) => {
            const sectionElement = section.selector
                ? document.querySelector(section.selector)
                : document.getElementById(section.id);

            if (!sectionElement) return;
            sectionElement.innerHTML = this.getSectionInnerHTML(
                parsedState.sections[section.id],
                section.selector,
            );
        });

        const resolvedCartItemKey = this.resolveRecommendationKey(
            parsedState?.key,
            parsedState,
        );
        window.lastAddedCartItemKey = resolvedCartItemKey || parsedState?.key;
        if (window.lastAddedCartItemKey) {
            window.cartDrawerLockedRecommendationKey =
                window.lastAddedCartItemKey;
        }
        const applyRecommendationFilter = () =>
            this.applyRecommendationFilter(
                window.lastAddedCartItemKey,
                parsedState,
            );

        setTimeout(() => {
            this.querySelector("#CartDrawer-Overlay").addEventListener(
                "click",
                this.close.bind(this),
            );
            this.open();

            const cartNotification =
                document.querySelector("cart-notification");
            if (
                cartNotification &&
                typeof cartNotification.renderContents === "function" &&
                parsedState?.sections?.["cart-notification-product"]
            ) {
                cartNotification.renderContents(parsedState);
            }

            applyRecommendationFilter();
            requestAnimationFrame(applyRecommendationFilter);

            this.enforceOnePerCustomerLimits();
        });
    }

    applyRecommendationFilter(keyFromState, parsedState) {
        const resolvedKey = this.resolveRecommendationKey(
            keyFromState || window.lastAddedCartItemKey,
            parsedState,
        );
        const recommendationNodes = document.querySelectorAll(
            "#CartDrawer .cart-item__recommendation[data-recommendation-key]",
        );
        const nodeKeys = new Set(
            Array.from(recommendationNodes).map(
                (node) => node.dataset.recommendationKey,
            ),
        );
        let activeLockedKey = window.cartDrawerLockedRecommendationKey;
        if (activeLockedKey && !nodeKeys.has(activeLockedKey)) {
            if (resolvedKey && nodeKeys.has(resolvedKey)) {
                window.cartDrawerLockedRecommendationKey = resolvedKey;
                activeLockedKey = resolvedKey;
            } else {
                window.cartDrawerLockedRecommendationKey = "";
                activeLockedKey = "";
            }
        }
        const key = activeLockedKey || resolvedKey;

        if (!key) {
            recommendationNodes.forEach((node) => {
                node.style.display = "none";
            });
            return;
        }

        recommendationNodes.forEach((node) => {
            node.style.display = "none";
        });

        recommendationNodes.forEach((node) => {
            const match = node.dataset.recommendationKey === key;
            if (match) {
                node.style.display = "";
            }
        });
    }

    resolveRecommendationKey(rawKey, parsedState) {
        const items = parsedState?.items;
        if (!items || !Array.isArray(items) || items.length === 0) {
            return rawKey || null;
        }

        if (rawKey) {
            const matchedItem = items.find((item) => item?.key === rawKey);
            if (matchedItem) {
                const props = this.getLineItemProperties(matchedItem);
                if (this.isAddonLineItem(props) && props._parent_key) {
                    return props._parent_key;
                }
                return rawKey;
            }
        }

        const variantId = parsedState?.id || this.productId;
        if (variantId) {
            const byVariant = items.find((item) => {
                const props = this.getLineItemProperties(item);
                return (
                    !this.isAddonLineItem(props) &&
                    String(item?.variant_id) === String(variantId)
                );
            });
            if (byVariant?.key) return byVariant.key;
        }

        const fallbackNonAddon = items.find((item) => {
            const props = this.getLineItemProperties(item);
            return !this.isAddonLineItem(props);
        });
        if (fallbackNonAddon?.key) return fallbackNonAddon.key;

        const fallbackByVariant = this.resolveKeyByVariantId(variantId);
        return fallbackByVariant || rawKey || null;
    }

    resolveKeyByVariantId(variantId) {
        if (!variantId) return null;
        const variantPrefix = `${variantId}:`;
        const nodes = document.querySelectorAll(
            "#CartDrawer .cart-item__recommendation[data-recommendation-key]",
        );

        for (const node of nodes) {
            const recommendationKey = node.dataset.recommendationKey;
            if (recommendationKey && recommendationKey.startsWith(variantPrefix)) {
                return recommendationKey;
            }
        }

        return null;
    }

    getLineItemProperties(item) {
        const raw = item?.properties;
        if (!raw) return {};
        if (Array.isArray(raw)) {
            return raw.reduce((acc, pair) => {
                if (pair && pair.name != null) acc[String(pair.name)] = pair.value;
                return acc;
            }, {});
        }
        return raw;
    }

    isAddonLineItem(properties) {
        return (
            properties?._is_protection === "true" ||
            properties?._is_extra_item === "true" ||
            properties?._is_extra_item_protection === "true"
        );
    }

    enforceOnePerCustomerLimits() {
        const items = this.querySelectorAll('[data-one-per-customer="true"]');
        items.forEach((el) => {
            const qtyInput = el.querySelector(".quantity__input");
            if (!qtyInput) return;
            if (parseInt(qtyInput.value, 10) <= 1) return;
            qtyInput.value = 1;
            qtyInput.dispatchEvent(new Event("change", { bubbles: true }));
        });
    }

    getSectionInnerHTML(html, selector = ".shopify-section") {
        return new DOMParser()
            .parseFromString(html, "text/html")
            .querySelector(selector).innerHTML;
    }

    getSectionsToRender() {
        return [
            {
                id: "cart-drawer",
                selector: "#CartDrawer",
            },
            {
                id: "cart-notification-product",
            },
            {
                id: "cart-notification-button",
            },
            {
                id: "cart-icon-bubble",
            },
        ];
    }

    getSectionDOM(html, selector = ".shopify-section") {
        return new DOMParser()
            .parseFromString(html, "text/html")
            .querySelector(selector);
    }

    setActiveElement(element) {
        this.activeElement = element;
    }
}

customElements.define("cart-drawer", CartDrawer);

window.applyCartDrawerRecommendationFilter = (key, parsedState) => {
    const cartDrawer = document.querySelector("cart-drawer");
    if (
        cartDrawer &&
        typeof cartDrawer.applyRecommendationFilter === "function"
    ) {
        cartDrawer.applyRecommendationFilter(key, parsedState);
    }
};

class CartDrawerItems extends CartItems {
    getSectionsToRender() {
        return [
            {
                id: "CartDrawer",
                section: "cart-drawer",
                selector: ".drawer__inner",
            },
            {
                id: "cart-icon-bubble",
                section: "cart-icon-bubble",
                selector: ".shopify-section",
            },
        ];
    }
}

customElements.define("cart-drawer-items", CartDrawerItems);

// Automatically open the cart drawer if ?open_cart=true query param is present on page load
if (new URLSearchParams(window.location.search).get('open_cart') === 'true') {
    const openCartDrawer = () => {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
            // Clean up the URL search params so a page refresh doesn't keep opening it
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('open_cart');
            window.history.replaceState({}, '', newUrl.toString());

            // Open the drawer
            setTimeout(() => {
                cartDrawer.open();
            }, 300);
        }
    };
    if (document.readyState === 'loading') {
        window.addEventListener('DOMContentLoaded', openCartDrawer);
    } else {
        openCartDrawer();
    }
}
